import { formatPhoneDisplay } from '@/lib/transferBanner';
import { isPhoneCallbackNotification } from '@/lib/callbackTicket';
import type { StaffNotification } from '@/services/notificationService';

/**
 * Wording of the bell's times. All inputs are server-clock ms (event and
 * overview times are the server's), so a salon PC whose clock is off still
 * counts correctly.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function parse(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** Ticking duration: "0:07", "1:42", "1:02:05". */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
}

/** Waiting time: ticking below an hour, coarse above ("1 h 5 min", "2 Tg."). */
export function formatWait(ms: number): string {
  if (ms < HOUR) return formatClock(ms);
  if (ms < DAY) {
    const hours = Math.floor(ms / HOUR);
    const minutes = Math.floor((ms % HOUR) / MINUTE);
    return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  }
  return `${Math.floor(ms / DAY)} Tg.`;
}

/** "seit 5 min", "seit 2 h", "seit 3 Tg." */
export function formatSince(sinceIso: string | null | undefined, now: number): string {
  const since = parse(sinceIso);
  if (since === null) return '';
  const ms = Math.max(0, now - since);
  if (ms < MINUTE) return 'gerade eben';
  if (ms < HOUR) return `seit ${Math.floor(ms / MINUTE)} min`;
  if (ms < DAY) return `seit ${Math.floor(ms / HOUR)} h`;
  return `seit ${Math.floor(ms / DAY)} Tg.`;
}

function clock(t: number): string {
  return new Date(t).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function startOfDay(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** "gerade eben", "vor 3 min", "14:02", "gestern 14:02", "28.09. 14:02". */
export function formatRelative(iso: string | null | undefined, now: number): string {
  const t = parse(iso);
  if (t === null) return '';
  const ms = now - t;
  if (ms < MINUTE) return 'gerade eben';
  if (ms < HOUR) return `vor ${Math.floor(ms / MINUTE)} min`;
  const today = startOfDay(now);
  if (t >= today) return clock(t);
  if (t >= today - DAY) return `gestern ${clock(t)}`;
  const d = new Date(t);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}. ${clock(t)}`;
}

export type DayGroup = 'Heute' | 'Gestern' | 'Älter';

export function dayGroup(iso: string | null | undefined, now: number): DayGroup {
  const t = parse(iso) ?? 0;
  const today = startOfDay(now);
  if (t >= today) return 'Heute';
  if (t >= today - DAY) return 'Gestern';
  return 'Älter';
}

export interface WindowState {
  expired: boolean;
  text: string;
}

/** Meta's 24-h reply window of a callback request (plan §4). */
export function formatReplyWindow(untilIso: string | null | undefined, now: number): WindowState | null {
  const until = parse(untilIso);
  if (until === null) return null;
  const left = until - now;
  if (left <= 0) return { expired: true, text: 'Fenster abgelaufen – bitte anrufen' };
  if (left < HOUR) return { expired: false, text: `Fenster noch ${Math.max(1, Math.floor(left / MINUTE))} min` };
  return { expired: false, text: `Fenster noch ${Math.floor(left / HOUR)} h` };
}

/** "Agent springt in 3:18 ein" — or that it is about to, once the deadline passed. */
export function formatAgentTakesOver(deadlineIso: string | null | undefined, now: number): string | null {
  const deadline = parse(deadlineIso);
  if (deadline === null) return null;
  const left = deadline - now;
  return left > 0 ? `Agent springt in ${formatClock(left)} ein` : 'Agent springt gleich ein';
}

export function elapsedSince(iso: string | null | undefined, now: number): number {
  const t = parse(iso);
  return t === null ? 0 : Math.max(0, now - t);
}

export function displayName(name: string | null | undefined, phone: string | null | undefined): string {
  const trimmed = name?.trim();
  if (trimmed) return trimmed;
  return phone ? formatPhoneDisplay(phone) : 'Unbekannt';
}

export function unreadLabel(count: number): string {
  if (count <= 0) return 'Benachrichtigungen';
  return count === 1 ? '1 ungelesene Benachrichtigung' : `${count} ungelesene Benachrichtigungen`;
}

/**
 * What a notification replaced its preview with once it was resolved: a
 * finished call must not read "Gespräch läuft · erledigt" (E2E 30.09.).
 */
const RESOLVED_DETAIL: Partial<Record<StaffNotification['kind'], string>> = {
  call_live: 'Gespräch beendet',
  whatsapp_channel_blocked: 'Nummer wieder erreichbar',
};

/** The one thing staff can do about a blocked number; the row's click does it. */
export const CHANNEL_BLOCKED_ACTION = 'Bei Meta prüfen';

/** Says which callback wish it is: the phone agent's ticket, not the WhatsApp „Ja, bitte melden“. */
export const PHONE_CALLBACK_LABEL = 'Rückrufwunsch (Telefon)';

/** Second line of a bell entry: preview, count, "erledigt", the agent line. */
export function notificationDetail(item: StaffNotification, showAgentNumber: boolean): string {
  if (item.kind === 'whatsapp_channel_blocked') return channelBlockedDetail(item);
  const parts: string[] = [];
  if (isPhoneCallbackNotification(item)) parts.push(PHONE_CALLBACK_LABEL);
  const resolvedText = item.resolved_at ? RESOLVED_DETAIL[item.kind] : undefined;
  if (resolvedText) parts.push(resolvedText);
  else if (item.preview) parts.push(item.preview);
  if (item.count > 1) parts.push(`${item.count}×`);
  if (item.resolved_at && !resolvedText) parts.push('erledigt');
  if (showAgentNumber && item.agent_number) parts.push(`über ${formatPhoneDisplay(item.agent_number)}`);
  return parts.join(' · ');
}

/**
 * Blocked number: the server's text already names number and reason, so no
 * „über …“ is added; while open it ends with what to do, once resolved it
 * says the number is back.
 */
function channelBlockedDetail(item: StaffNotification): string {
  if (item.resolved_at) return RESOLVED_DETAIL.whatsapp_channel_blocked ?? '';
  const what = item.preview || (item.agent_number ? formatPhoneDisplay(item.agent_number) : '');
  return [what, CHANNEL_BLOCKED_ACTION].filter(Boolean).join(' · ');
}
